import { NodeProps, XYPosition } from 'reactflow'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { AuthStatus, User } from 'shared/types'
import { IConnector } from 'shared/types/connector.type'
import {
  HoverableEdgeData,
  IAuthStore,
  IClientsStore,
  IConnectorsStore,
  IContextMenuStore,
  IHoverStore,
  IMouseDown,
  IPresCategoryStore,
  IPrewiewsStore,
  ISearchStore,
  ISelectionRectStore,
  ISelectionRectStoreData,
  ISimplifiedSlidesViewStore,
  IZoomPopupStore,
  PresCategories,
  SearchData,
} from './data.types'

export const useAuthStore = create<IAuthStore>()(
  devtools((set) => ({
    authStatus: AuthStatus.PENDING,
    user: null,
  }))
)

export const authStatusHandler = (authStatus: AuthStatus) => {
  useAuthStore.setState({ authStatus })
}

export const userHandler = (user: User) => {
  useAuthStore.setState({ user })
}

export const useMouseDown = create<IMouseDown>()(
  devtools((set) => ({
    isMouseDown: false,
  }))
)

export const mouseDownHandler = (newIsMouseDown: boolean) => {
  useMouseDown.setState({
    isMouseDown: newIsMouseDown,
  })
}

export const useConnectorsStore = create<IConnectorsStore>()(
  devtools((set) => ({
    connectors: {},
    setConnectors: (newConnectors: Record<string, IConnector>, updateCurrentValue: boolean) => {
      set((state) =>
        updateCurrentValue
          ? { connectors: { ...state.connectors, ...newConnectors } }
          : { connectors: newConnectors }
      )
    },
  }))
)

export const useClientsStore = create<IClientsStore>()(
  devtools((set) => ({
    clients: {},
    setClients: (newClients: Record<string, string>) => {
      set(() => ({ clients: newClients }))
    },
  }))
)

export const usePrewiewsStore = create<IPrewiewsStore>()(
  devtools((set) => ({
    previewsLineView: localStorage.getItem('previewsLineView') === 'true' || false,
    setPreviewsLineView: (newPreviewsLineView: boolean) => {
      localStorage.setItem('previewsLineView', String(newPreviewsLineView))
      set(() => ({ previewsLineView: newPreviewsLineView }))
    },
  }))
)

export const useSimplifiedSlidesView = create<ISimplifiedSlidesViewStore>()(
  devtools((set) => ({
    simplifiedSlidesView: localStorage.getItem('simplifiedSlidesView') === 'true' || false,
    setSimplifiedSlidesView: (newSimplifiedSlidesView: boolean) => {
      localStorage.setItem('simplifiedSlidesView', String(newSimplifiedSlidesView))
      set(() => ({ simplifiedSlidesView: newSimplifiedSlidesView }))
    },
    toggleSimplifiedSlidesView: () => {
      set((state) => {
        const newValue = !state.simplifiedSlidesView
        localStorage.setItem('simplifiedSlidesView', String(newValue))
        return { simplifiedSlidesView: newValue }
      })
    },
  }))
)

export const useContextMenuStore = create<IContextMenuStore>()(
  devtools((set) => ({
    contextMenuTarget: null,
    contextMenuPosition: null,
    closeContextMenu: () => {
      set(() => ({ contextMenuTarget: null, contextMenuPosition: null }))
    },
  }))
)

export const setContextMenu = (
  newContextMenuTarget: NodeProps | null,
  newPosition: XYPosition | null = null
) => {
  useContextMenuStore.setState({
    contextMenuTarget: newContextMenuTarget,
    contextMenuPosition: newPosition,
  })
}

export const getContextMenuData = () => {
  return useContextMenuStore.getState()
}

export const useSelectionRectStore = create<ISelectionRectStore>()(
  devtools((set) => ({
    rectSize: { width: 0, height: 0 },
    setRectSize: (newRectSize: { width: number; height: number }) => {
      set(() => ({ rectSize: newRectSize }))
    },
    selectedData: null,
  }))
)
export const selectionUpdateHandler = ({ rectSize, selectedData }: ISelectionRectStoreData) => {
  useSelectionRectStore.setState((state) => {
    let newSelectedData = state.selectedData
    if (selectedData === null) newSelectedData = null
    else if (selectedData !== undefined) {
      newSelectedData = {
        nodes: [...selectedData.nodes],
        edges: [...selectedData.edges],
      }
    }

    return {
      rectSize: rectSize !== undefined ? rectSize : state.rectSize,
      selectedData: newSelectedData,
    }
  })
}

export const getSelection = () => {
  return useSelectionRectStore.getState()
}

export const clearSelectionData = () => {
  useSelectionRectStore.setState({
    rectSize: { width: 0, height: 0 },
    selectedData: null,
  })
}

export const useZoomPopupStore = create<IZoomPopupStore>()(
  devtools((set) => ({
    zoomPopup: null,
    setZoomPopup: (newZoomPopup: string | null) => {
      set(() => ({ zoomPopup: newZoomPopup }))
    },
  }))
)

export const useHoverStore = create<IHoverStore>()(
  devtools((set) => ({
    hoverElement: null,
    setHoverElement: (hoverElement: NodeProps | HoverableEdgeData | null) => {
      set(() => ({ hoverElement: hoverElement }))
    },
  }))
)

export const getHoveredElement = () => {
  return useHoverStore.getState().hoverElement
}

export const usePresCategoryStore = create<IPresCategoryStore>()(
  devtools((set) => ({
    presCategory: PresCategories.ALL,
    setPresCategory: (newPresCategory: PresCategories) => {
      set(() => ({ presCategory: newPresCategory }))
    },
  }))
)

export const useSearchStore = create<ISearchStore>()(
  devtools((set) => ({
    searchData: {
      query: null,
      client: null,
      author: null,
    },
    setSearchData: (newData: SearchData) => {
      set((data) => {
        return { searchData: { ...data.searchData, ...newData } }
      })
    },
  }))
)
